import * as Immutable from 'immutable';
import * as t from 'io-ts';

import { ioTsEmail, ioTsRequired } from '../../shared/helpers/io-ts-helpers';
import { UserUXSettings } from '../../shared/helpers/user-settings';
import { Action, ImmutableInterface } from '../../types/';

export interface AuthenticationAction {
  data?: any;
  permissions?: string[];
  token?: any;
  user?: any;
  moment?: any;
  json?: any;
  redirectUrl?: string;
  challenge?: string;
  inquiryId?: string;
}

export interface BaseAuthenticationStore {
  user: AuthenticationStoreElement;
}
export type AuthenticationStore = ImmutableInterface<BaseAuthenticationStore>;

export interface BaseAuthenticationStoreElement {
  userId: string | null;
  isFetching: boolean;
  isFetchingLogout: boolean;
  forgotPasswordEmailSent: boolean;
  isPasswordReset: boolean;
  isLoggedIn: boolean;
  data: any;
  permissions: Immutable.List<string>;
  settings: any;
  uxSettings: UserUXSettings;
  isFetchingSettings: boolean;
  isFetchingReverify: boolean;
  isSelfieError: boolean;
  inquiryId: string;
  reverifySent: boolean;
  timeToLogout: number;
  hasLoggedInBefore: boolean;
  fetchingAuth: boolean;
  fetchingUserAgreements: boolean;
  fetchingChangePassword: boolean;
  userAgreements: any;
  needsRecertification: false;
  showMessage: boolean;
  redirectUrl?: string;
  configuration: {
    analytics: { trackingId: string | null };
  };
}
export type AuthenticationStoreElement = ImmutableInterface<BaseAuthenticationStoreElement>;

export type AuthenticationActionType = Action<AuthenticationAction>;

export const UserEducation = t.interface({
  university: t.union([t.string, t.null]),
  degree: t.union([t.string, t.null]),
  major: t.union([t.string, t.null]),
  graduationYear: t.union([t.number, t.null]),
  gpa: t.union([t.number, t.null]),
  maxGPA: t.union([t.number, t.null]),
  stem: t.union([t.boolean, t.null]),
});

export const UserEmployment = t.interface({
  employer: t.union([t.string, t.null]),
  business: t.union([t.string, t.null]),
  title: t.union([t.string, t.null]),
  description: t.union([t.string, t.null]),
  nonPublicInformation: t.union([t.boolean, t.null]),
  participationPermitted: t.union([t.boolean, t.null]),
  finance: t.union([t.boolean, t.null]),
  investmentAdvisory: t.union([t.boolean, t.null]),
  brokerage: t.union([t.boolean, t.null]),
  fundamentalResearch: t.union([t.boolean, t.null]),
  proprietaryTrading: t.union([t.boolean, t.null]),
  serviceEngagement: t.union([t.boolean, t.null]),
  employerServices: t.union([t.string, t.null]),
  exposureStatus: t.union([t.boolean, t.null]),
  exposureDetails: t.union([t.string, t.null]),
});

export const UserAccountStatus = t.interface({
  approved: t.boolean,
  verified: t.boolean,
  dateApproved: t.union([t.string, t.null]),
  dateVerified: t.union([t.string, t.null]),
  dateCreated: t.union([t.string, t.null]),
  id: t.union([t.string, t.null]),
});

export const UserData = t.interface({
  firstName: ioTsRequired,
  lastName: ioTsRequired,
  telephone: t.union([t.string, t.null]),
  email: t.intersection([ioTsRequired, ioTsEmail]),
  education: t.union([UserEducation, t.null]),
  employment: t.union([UserEmployment, t.null]),
  settings: t.interface({
    allowTracking: t.union([t.boolean, t.null]),
    communication: t.interface({
      allowSMS: t.union([t.boolean, t.null]),
    }),
  }),
});

export const UserFullName = t.partial({
  fullName: t.union([t.string, t.null]),
});

export const UserLevel = t.partial({
  level: t.union([t.string, t.null]),
});

export const UserAddress = t.interface({
  address: t.interface({
    country: ioTsRequired,
    street: t.union([t.string, t.null]),
    city: t.union([t.string, t.null]),
    state: t.union([t.string, t.null]),
    postalCode: t.union([t.string, t.null]),
  }),
});

export const UserInfo = t.intersection([UserAddress, UserData, UserAccountStatus, UserFullName, UserLevel]);

export const UserSettingsInfo = t.interface({
  delay: t.number,
  universe: t.string,
  truncation: t.number,
  region: t.string,
  language: t.string,
  decay: t.number,
  neutralization: t.string,
  pasteurization: t.union([t.string, t.undefined]),
  unitHandling: t.union([t.string, t.undefined]),
  nanHandling: t.union([t.string, t.undefined]),
  testPeriod: t.union([t.string, t.undefined]),
  maxTrade: t.union([t.string, t.undefined]),
  maxPosition: t.union([t.string, t.undefined]),
});
